import { FunnelIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import {
    EnrichedItemPublication,
    matchesPublicationFilter,
    usePublicationStatus,
} from '@/hooks/usePublicationStatus';
import { useTranslation } from '@/hooks/useTranslation';

export type PublicationStatusIndicatorProps = {
    primaryKey: string;
    /**
     * Storybook / test seam. Production leaves these unset and uses
     * {@link usePublicationStatus}.
     */
    status?: EnrichedItemPublication[];
    isLoading?: boolean;
};

const OrganizationCard = ({ publication }: { publication: EnrichedItemPublication }) => {
    const { formatDate } = useDateFormatter();
    const organization = publication.organization;
    if (!organization) {
        return null;
    }

    return (
        <div
            id={`publication_${publication.destinationId}`}
            className="contact-box"
            data-testid={`publication-${publication.destinationId}`}
        >
            <div className="contact-image">
                {organization.imageUrl ? (
                    <img alt={organization.name} src={organization.imageUrl} />
                ) : (
                    <span className="syncons syncons-contacts" />
                )}
            </div>
            <div className="contact-text">
                <div className="company small">
                    <i className="syncons syncons-home" />
                    {organization.name}
                </div>
                {publication.publishedAt != null && (
                    <div className="address small">
                        <i className="syncons syncons-history" />
                        {formatDate(publication.publishedAt, 'medium')}
                    </div>
                )}
                {publication.publishedBy && (
                    <div className="address small">
                        <i className="syncons syncons-user" />
                        {publication.publishedBy}
                    </div>
                )}
                {organization.address && (
                    <div className="address small">
                        <i className="syncons syncons-location" />
                        {organization.address}
                    </div>
                )}
                {organization.email && (
                    <div className="email small">
                        <i className="syncons syncons-email" />
                        {organization.email}
                    </div>
                )}
                {organization.url && (
                    <div className="url small">
                        <i className="syncons syncons-globe" />
                        {organization.url}
                    </div>
                )}
            </div>
        </div>
    );
};

const ChannelCard = ({ publication }: { publication: EnrichedItemPublication }) => {
    const { formatDate } = useDateFormatter();
    const channel = publication.communicationChannel;
    if (!channel) {
        return null;
    }

    const title = channel.subDestinationLabel || channel.subDestinationKey || channel.name;

    return (
        <div
            id={`publication_${publication.destinationId}`}
            className="contact-box"
            data-testid={`publication-channel-${publication.destinationId}`}
        >
            <div className="contact-image">
                <span className="syncons syncons-channels" />
            </div>
            <div className="contact-text">
                <div className="company small">
                    <i className="syncons syncons-home" />
                    {title}
                </div>
                {publication.publishedAt != null && (
                    <div className="address small">
                        <i className="syncons syncons-history" />
                        {formatDate(publication.publishedAt, 'medium')}
                    </div>
                )}
                {publication.publishedBy && (
                    <div className="address small">
                        <i className="syncons syncons-user" />
                        {publication.publishedBy}
                    </div>
                )}
                {channel.subDestinationKey && (
                    <>
                        <div className="url small">
                            <i className="syncons syncons-channels" />
                            {channel.name}
                        </div>
                        <div className="url small">
                            <i className="syncons syncons-double-arrows-right" />
                            {channel.subDestinationKey}
                        </div>
                    </>
                )}
                {channel.planLabel && (
                    <div className="address small">
                        <i className="syncons syncons-options" />
                        {channel.planLabel}
                    </div>
                )}
                {channel.type && (
                    <div className="email small">
                        <i className="syncons syncons-exchange" />
                        {channel.type}
                    </div>
                )}
            </div>
        </div>
    );
};

type PublicationStatusViewProps = {
    status: EnrichedItemPublication[];
    isLoading: boolean;
};

const PublicationStatusView = ({ status, isLoading }: PublicationStatusViewProps) => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState('');

    const publicationsByOrg = useMemo(
        () => status.filter((publication) => publication.organization),
        [status],
    );
    const publicationsByChannel = useMemo(
        () => status.filter((publication) => publication.communicationChannel),
        [status],
    );

    const filteredOrgPublications = useMemo(
        () => publicationsByOrg.filter((publication) => matchesPublicationFilter(publication, filter)),
        [publicationsByOrg, filter],
    );
    const filteredChannelPublications = useMemo(
        () => publicationsByChannel.filter((publication) => matchesPublicationFilter(publication, filter)),
        [publicationsByChannel, filter],
    );

    const showNoMatches =
        !isLoading
        && filteredOrgPublications.length < 1
        && filteredChannelPublications.length < 1;

    return (
        <div
            id="publicationStatus"
            className="tw-h-full tw-flex-1 tw-flex tw-flex-col tw-overflow-auto"
            data-testid="publication-status-indicator"
        >
            <div className="tw-p-4">
                <label className="tw-block tw-mb-1 tw-text-t-sm" htmlFor="recipient-search-input">
                    {t('SEARCH_CONTACTS_LABEL')}
                </label>
                <Input
                    id="recipient-search-input"
                    name="recipient-search-input"
                    type="text"
                    icon={FunnelIcon}
                    placeholder={t('SEARCH')}
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                />
            </div>
            {isLoading && (
                <div className="tw-flex tw-justify-center tw-py-6" data-testid="publication-status-loading">
                    <Loader />
                </div>
            )}
            {!isLoading && (
                <>
                    <div id="publicationsListByOrg" className="tw-flex tw-flex-wrap">
                        {showNoMatches && (
                            <span data-testid="publication-status-no-matches">{t('NO_MATCHES')}</span>
                        )}
                        {filteredOrgPublications.map((publication) => (
                            <div
                                key={`org-${publication.destinationId}-${publication.subDestination ?? ''}`}
                                className="tw-w-full md:tw-w-1/2 lg:tw-w-1/3"
                            >
                                <OrganizationCard publication={publication} />
                            </div>
                        ))}
                    </div>
                    <div id="publicationsListByChannel" className="tw-flex tw-flex-wrap">
                        {filteredChannelPublications.map((publication) => (
                            <div
                                key={`channel-${publication.destinationId}-${publication.subDestination ?? ''}`}
                                className="tw-w-full md:tw-w-1/2 lg:tw-w-1/3"
                            >
                                <ChannelCard publication={publication} />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const ConnectedPublicationStatusIndicator = ({ primaryKey }: { primaryKey: string }) => {
    const { status, isLoading } = usePublicationStatus(primaryKey);
    return <PublicationStatusView status={status} isLoading={isLoading} />;
};

/**
 * LAX-10152: React stand-in for {@code tpl/editor/publication-status.tpl.html}.
 * Dropped into the Item Editor publications tab in place of the AngularJS
 * {@code PublicationStatusController} island.
 */
const PublicationStatusIndicator = ({
    primaryKey,
    status,
    isLoading,
}: PublicationStatusIndicatorProps) => {
    if (status !== undefined) {
        return <PublicationStatusView status={status} isLoading={isLoading ?? false} />;
    }
    return <ConnectedPublicationStatusIndicator primaryKey={primaryKey} />;
};

PublicationStatusIndicator.displayName = 'PublicationStatusIndicator';

export { PublicationStatusIndicator, PublicationStatusView };
